/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('.btn-buy')

    this.form.addEventListener('submit', event => this.onSubmit(event))

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event)))

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))
  }

  async onChangeProductOption (event) {
    event.preventDefault()

    const optionGroup = event.target.closest('[data-product-option-group]')
    const optionSelected = optionGroup?.querySelector('[data-product-option-selected]')
    if (optionSelected) optionSelected.textContent = event.target.value

    const selectedOptions = []

    this.productOptions.forEach(elem => {
      if (elem.type === 'radio' && elem.checked) {
        selectedOptions.push(elem.value)
      } else if (elem.type === 'select-one') {
        selectedOptions.push(elem.value)
      }
    })

    // console.log(selectedOptions)

    const selectedVariant = JSON.parse(this.form.dataset.variants).find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    )

    console.log(selectedVariant)

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)

    if (selectedVariant) {
      this.querySelector('[name="id"]').value = selectedVariant.id

      if (this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
        detail: selectedVariant
      }))
    }
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) return

    const comparePrice = this.form.querySelector('.product-price-compare')
    const finalPrice = this.form.querySelector('.product-price-final')
    const compareBadge = this.form.querySelector('.product-sale-badge')
    const soldOutBadge = this.form.querySelector('.product-sold-out-badge')

    if (selectedVariant.compare_at_price > selectedVariant.price) {
      comparePrice.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price)
      comparePrice.removeAttribute('hidden')
      finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}}</span>${window.Shopify.formatMoney(selectedVariant.price)}`
      finalPrice.classList.add('product-price-final-sale')
      compareBadge.removeAttribute('hidden')
      if (compareBadge.dataset.discountType === 'percentage') {
        const savings = 100 - (Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100)) + '%'
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      } else {
        const savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price)
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      }
    } else {
      comparePrice.textContent = ''
      comparePrice.setAttribute('hidden', 'hidden')
      finalPrice.innerHTML = window.Shopify.formatMoney(selectedVariant.price)
      finalPrice.classList.remove('product-price-final-sale')
      compareBadge.textContent = ''
      compareBadge.setAttribute('hidden', 'hidden')
    }

    if (selectedVariant.available) {
      soldOutBadge.setAttribute('hidden', 'hidden')
    } else {
      soldOutBadge.removeAttribute('hidden')
    }
  }

  adjustButtons (selectedVariant) {
    if (selectedVariant) {
      if (selectedVariant.available) {
        this.atcBtn.removeAttribute('disabled')
        this.buyBtn?.removeAttribute('disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.add_to_cart
      } else {
        this.atcBtn.setAttribute('disabled', 'disabled')
        this.buyBtn?.setAttribute('disabled', 'disabled')
        this.atcBtn.innerHTML = window.theme.locales.product.sold_out
      }
    } else {
      this.atcBtn.setAttribute('disabled', 'disabled')
      this.buyBtn?.setAttribute('disabled', 'disabled')
      this.atcBtn.innerHTML = window.theme.locales.product.unavailable
    }
  }

  async onSubmit (event) {
    event.preventDefault()

    this.atcBtn.classList.add('loading')
    this.atcBtn.disabled = true
    this.atcBtn.setAttribute('aria-busy', 'true')

    try {
      const formData = new FormData(this.form)
      const accessoryCheckboxes = this.form.querySelectorAll('.accessory-checkbox:checked')

      if (accessoryCheckboxes.length > 0) {
        const items = []

        // Main product
        const mainId = this.form.querySelector('[name="id"]').value
        const mainQty = this.form.querySelector('[name="quantity"]')?.value || 1
        const mainItem = { id: mainId, quantity: parseInt(mainQty) }
        const propertyInputs = this.form.querySelectorAll('[name^="properties["]:not([type="radio"]):not([type="checkbox"]), [name^="properties["]:checked')

        if (propertyInputs.length > 0) {
          mainItem.properties = {}
          propertyInputs.forEach(input => {
            const name = input.name.match(/properties\[(.*?)\]/)?.[1]
            if (name) mainItem.properties[name] = input.value
          })
        }

        const sellingPlan = this.form.querySelector('[name="selling_plan"]')?.value
        if (sellingPlan) mainItem.selling_plan = sellingPlan
        items.push(mainItem)

        // Accessories
        accessoryCheckboxes.forEach(chk => {
          const item = chk.closest('[data-accessory-item]')
          const qtyInput = item?.querySelector('[data-accessory-qty-input]')
          items.push({ id: chk.value, quantity: parseInt(qtyInput?.value || 1, 10) })
        })

        await document.querySelector('cart-container').add({ items })
      } else {
        await document.querySelector('cart-container').add(formData)
      }
    } catch (error) {
      console.error('Unable to add product:', error)
    } finally {
      this.atcBtn.classList.remove('loading')
      this.atcBtn.disabled = false
      this.atcBtn.setAttribute('aria-busy', 'false')
    }
  }

  onClickBuyBtn (event) {
    event?.preventDefault()
    this.buyBtn.classList.add('loading')
    this.buyBtn.disabled = true
    this.buyBtn.setAttribute('aria-busy', 'true')
    const variantId = this.form.querySelector('[name="id"]').value
    const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1)
    requestAnimationFrame(() => {
      window.location.href = `/cart/${variantId}:${qty}`
    })
  }
}
customElements.define('product-form', ProductForm)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const initialSlide = Number(this.dataset.initialSlide)

    /** @type {Swiper} */
    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions())

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions())

    this.main.slideTo(initialSlide)

    this.setStickyPosition()
    this.listenVariantChange()
    this.initZoom()
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: 'auto',
      spaceBetween: 11,
      freeMode: true,
      watchSlidesProgress: true,
      breakpoints: {
        768: {
          spaceBetween: 22
        }
      }
    }
    return options
  }

  mainOptions () {
    const navRoot = this.querySelector('.product-media-gallery__thumbs-row')

    /** @type {SwiperOptions} */
    const options = {
      speed: Number(this.dataset.speed),
      rewind: true,
      navigation: {
        nextEl: navRoot?.querySelector('.swiper-button-next') || null,
        prevEl: navRoot?.querySelector('.swiper-button-prev') || null
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      }
    }
    return options
  }

  initZoom () {
    if (this.dataset.fullScreen !== 'true') return

    const overlay = document.createElement('div')
    overlay.className = 'product-media-lightbox'
    overlay.hidden = true
    overlay.innerHTML = `
      <button type="button" class="product-media-lightbox__close" aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <img class="product-media-lightbox__img" alt="">
    `
    document.body.appendChild(overlay)

    const imgEl = overlay.querySelector('.product-media-lightbox__img')
    const closeBtn = overlay.querySelector('.product-media-lightbox__close')

    const openZoom = (src, alt = '') => {
      if (!src) return
      imgEl.src = src
      imgEl.alt = alt
      overlay.hidden = false
      document.body.classList.add('product-media-lightbox-open')
    }

    const closeZoom = () => {
      overlay.hidden = true
      document.body.classList.remove('product-media-lightbox-open')
      imgEl.removeAttribute('src')
    }

    this.querySelectorAll('.swiper-main .swiper-slide[data-zoom-src]').forEach((slide) => {
      const img = slide.querySelector('img')
      if (!img) return

      img.classList.add('product-media-gallery__zoomable')
      img.addEventListener('click', () => {
        openZoom(slide.dataset.zoomSrc, img.alt)
      })
    })

    this.querySelector('.product-media-gallery-zoom')?.addEventListener('click', () => {
      const activeSlide = this.main.slides[this.main.activeIndex]
      const activeImg = activeSlide?.querySelector('img')
      openZoom(activeSlide?.dataset.zoomSrc || activeImg?.src, activeImg?.alt)
    })

    closeBtn.addEventListener('click', closeZoom)
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) closeZoom()
    })
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !overlay.hidden) closeZoom()
    })
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      if (this.closest('.product-main__media-col--with-features')) return

      const headerGroup = document.querySelector('#header-group.sticky-top')
      if (!headerGroup) return

      const updateTop = () => {
        this.style.top = `${headerGroup.clientHeight + 20}px`
      }

      updateTop()
      window.addEventListener('resize', updateTop, { passive: true })
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      if (selectedVariant.featured_media) {
        this.main.slideTo(selectedVariant.featured_media.position - 1)
      }
    }, false)
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)

const initProductPageMotion = (root = document) => {
  if (!document.body.classList.contains('page-type-product')) return
  if (!window.gsap || !window.ScrollTrigger) return
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  const scope = root instanceof Element ? root : document
  const gsap = window.gsap
  gsap.registerPlugin(window.ScrollTrigger)

  const selectAll = (selector) => {
    const elements = []
    if (scope.matches?.(selector)) elements.push(scope)
    scope.querySelectorAll?.(selector).forEach((element) => elements.push(element))
    return elements
  }

  const reveal = (selector, fromVars = {}, options = {}) => {
    selectAll(selector).forEach((element) => {
      if (element.dataset.productMotionInit === 'true') return
      if (options.skipStaggerChildren && element.closest('[data-product-stagger]') !== element && element.closest('[data-product-stagger]')) return
      if (options.skipContaining && element.querySelector(options.skipContaining)) return
      element.dataset.productMotionInit = 'true'

      gsap.fromTo(element, {
        autoAlpha: 0,
        y: 28,
        ...fromVars
      }, {
        autoAlpha: 1,
        x: 0,
        y: 0,
        duration: 0.9,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: element,
          start: 'top 86%',
          once: true
        }
      })
    })
  }

  reveal('[data-product-reveal]', {}, { skipStaggerChildren: true })
  reveal('.page-type-product #main > .shopify-section:not(:first-child)', { y: 40 }, {
    skipContaining: '[data-np-social], [data-np-feature]'
  })

  selectAll('[data-product-stagger]').forEach((container) => {
    if (container.dataset.productStaggerInit === 'true') return
    container.dataset.productStaggerInit = 'true'

    const items = container.querySelectorAll('[data-product-stagger-item], [data-product-reveal]')

    if (!items.length) return

    gsap.fromTo(items, {
      autoAlpha: 0,
      x: 30
    }, {
      autoAlpha: 1,
      x: 0,
      duration: 0.75,
      ease: 'power3.out',
      stagger: 0.08,
      scrollTrigger: {
        trigger: container,
        start: 'top 84%',
        once: true
      }
    })
  })

  if (!window.matchMedia('(min-width: 900px)').matches) return

  selectAll('[data-product-parallax]').forEach((element) => {
    if (element.dataset.productParallaxInit === 'true') return
    if (element.closest('.product-media-gallery')) return
    element.dataset.productParallaxInit = 'true'

    gsap.to(element, {
      yPercent: element.closest('.product-media-gallery') ? 5 : -7,
      ease: 'none',
      scrollTrigger: {
        trigger: element.closest('[data-product-section]') || element.closest('.shopify-section') || element.closest('section') || element,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    })
  })
}

const initProductComponentInteractions = (root = document) => {
  if (!document.body.classList.contains('page-type-product')) return

  const scope = root instanceof Element ? root : document
  const gsap = window.gsap

  const selectAll = (selector) => {
    const elements = []
    if (scope.matches?.(selector)) elements.push(scope)
    scope.querySelectorAll?.(selector).forEach((element) => elements.push(element))
    return elements
  }

  selectAll('[data-product-media-tilt]').forEach((wrapper) => {
    if (wrapper.dataset.productTiltInit === 'true') return
    wrapper.dataset.productTiltInit = 'true'

    wrapper.addEventListener('pointermove', (event) => {
      if (!gsap || !window.matchMedia('(pointer: fine)').matches) return

      const rect = wrapper.getBoundingClientRect()
      const x = (event.clientX - rect.left) / rect.width - 0.5
      const y = (event.clientY - rect.top) / rect.height - 0.5

      gsap.to(wrapper, {
        rotateX: y * -3,
        rotateY: x * 4,
        scale: 1.012,
        transformPerspective: 900,
        transformOrigin: 'center',
        duration: 0.35,
        ease: 'power2.out'
      })
    })

    wrapper.addEventListener('pointerleave', () => {
      if (!gsap) return

      gsap.to(wrapper, {
        rotateX: 0,
        rotateY: 0,
        scale: 1,
        duration: 0.55,
        ease: 'power3.out'
      })
    })
  })

  selectAll('[data-product-interactive]').forEach((element) => {
    if (element.dataset.productHoverInit === 'true') return
    element.dataset.productHoverInit = 'true'

    element.addEventListener('pointerenter', () => {
      if (!gsap || element.matches('[disabled], [aria-disabled="true"]')) return

      gsap.to(element, {
        y: element.matches('.btn, label, select') ? -1 : -4,
        scale: element.matches('.btn, label, select') ? 1.025 : 1.018,
        duration: 0.24,
        ease: 'power2.out'
      })
    })

    element.addEventListener('pointerleave', () => {
      if (!gsap) return

      gsap.to(element, {
        y: 0,
        scale: 1,
        duration: 0.32,
        ease: 'power3.out'
      })
    })

    element.addEventListener('pointerdown', () => {
      if (!gsap || element.matches('[disabled], [aria-disabled="true"]')) return

      gsap.to(element, {
        scale: 0.98,
        duration: 0.08,
        ease: 'power2.out'
      })
    })

    element.addEventListener('pointerup', () => {
      if (!gsap || element.matches('[disabled], [aria-disabled="true"]')) return

      gsap.to(element, {
        scale: 1.018,
        duration: 0.18,
        ease: 'back.out(2)'
      })
    })
  })

  selectAll('[data-world-cup-sale]').forEach((banner) => {
    if (banner.dataset.worldCupSaleInit === 'true') return
    banner.dataset.worldCupSaleInit = 'true'

    const codeButton = banner.querySelector('[data-copy-code]')
    const status = banner.querySelector('.product-world-cup-sale__copy-status')

    const showCopied = () => {
      if (!codeButton || !status) return

      const copiedLabel = codeButton.dataset.copiedLabel || 'Copied!'
      codeButton.classList.add('is-copied')
      status.textContent = copiedLabel
      status.classList.add('is-visible')
      clearTimeout(banner.worldCupCopyTimer)
      banner.worldCupCopyTimer = setTimeout(() => {
        codeButton.classList.remove('is-copied')
        status.classList.remove('is-visible')
      }, 1800)
    }

    const fallbackCopy = (text) => {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      textarea.remove()
    }

    codeButton?.addEventListener('click', async () => {
      const code = codeButton.dataset.copyCode || codeButton.textContent.trim()

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code)
        } else {
          fallbackCopy(code)
        }
        showCopied()
      } catch (error) {
        fallbackCopy(code)
        showCopied()
      }
    })

    const daysEl = banner.querySelector('[data-countdown-days]')
    const hoursEl = banner.querySelector('[data-countdown-hours]')
    const minutesEl = banner.querySelector('[data-countdown-minutes]')
    const secondsEl = banner.querySelector('[data-countdown-seconds]')
    let remaining = Number(banner.dataset.countdownTotal || 0)

    const renderCountdown = () => {
      const days = Math.floor(remaining / 86400)
      const hours = Math.floor((remaining % 86400) / 3600)
      const minutes = Math.floor((remaining % 3600) / 60)
      const seconds = remaining % 60

      if (daysEl) daysEl.textContent = String(days)
      if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0')
      if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0')
      if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0')
    }

    renderCountdown()

    if (remaining > 0) {
      banner.worldCupCountdownTimer = setInterval(() => {
        remaining = Math.max(remaining - 1, 0)
        renderCountdown()

        if (remaining === 0) {
          clearInterval(banner.worldCupCountdownTimer)
        }
      }, 1000)
    }
  })

  selectAll('[data-product-accessories]').forEach((accessoriesRoot) => {
    if (accessoriesRoot.dataset.productAccessoryInit === 'true') return
    accessoriesRoot.dataset.productAccessoryInit = 'true'

    const syncItemState = (item) => {
      const checkbox = item.querySelector('.accessory-checkbox')
      const qtyWrap = item.querySelector('.accessory-item__qty')
      const qtyInput = item.querySelector('[data-accessory-qty-input]')
      const minusBtn = item.querySelector('[data-accessory-qty-minus]')
      const plusBtn = item.querySelector('[data-accessory-qty-plus]')
      const isSelected = Boolean(checkbox?.checked)

      item.classList.toggle('is-selected', isSelected)

      if (qtyWrap) {
        qtyWrap.hidden = !isSelected
      }

      if (qtyInput) {
        qtyInput.disabled = !isSelected
        if (!isSelected) {
          qtyInput.value = '1'
        }
      }

      if (minusBtn) {
        minusBtn.disabled = !isSelected || parseInt(qtyInput?.value || 1, 10) <= 1
      }

      if (plusBtn) {
        plusBtn.disabled = !isSelected
      }
    }

    const syncAllItems = () => {
      accessoriesRoot.querySelectorAll('[data-accessory-item]').forEach(syncItemState)
    }

    accessoriesRoot.addEventListener('change', (event) => {
      if (!event.target.matches('.accessory-checkbox')) return

      const item = event.target.closest('[data-accessory-item]')
      if (!item) return

      syncItemState(item)
    })

    accessoriesRoot.addEventListener('click', (event) => {
      const toggleBtn = event.target.closest('.js-toggle-accessories')
      if (toggleBtn) {
        const extras = accessoriesRoot.querySelectorAll('.extra-accessory')
        const expanded = toggleBtn.getAttribute('data-expanded') === 'true'
        const textEl = toggleBtn.querySelector('.accessories-toggle__text')

        extras.forEach((el) => {
          el.classList.toggle('accessory-item--hidden', expanded)
        })

        toggleBtn.setAttribute('data-expanded', (!expanded).toString())
        toggleBtn.classList.toggle('is-expanded', !expanded)

        if (textEl) {
          textEl.textContent = expanded
            ? `Show (${extras.length}) more`
            : 'Show less'
        }

        return
      }

      const minusBtn = event.target.closest('[data-accessory-qty-minus]')
      const plusBtn = event.target.closest('[data-accessory-qty-plus]')

      if (minusBtn || plusBtn) {
        event.preventDefault()
        event.stopPropagation()

        const item = (minusBtn || plusBtn).closest('[data-accessory-item]')
        const qtyInput = item?.querySelector('[data-accessory-qty-input]')
        if (!item || !qtyInput || qtyInput.disabled) return

        const current = parseInt(qtyInput.value || 1, 10)
        qtyInput.value = minusBtn ? Math.max(current - 1, 1) : current + 1
        syncItemState(item)
        return
      }

      const titleLink = event.target.closest('.accessory-item__title')
      const mediaLink = event.target.closest('.accessory-item__media')
      const rowTarget = titleLink || mediaLink

      if (rowTarget) {
        event.preventDefault()
        const item = rowTarget.closest('[data-accessory-item]')
        const checkbox = item?.querySelector('.accessory-checkbox')
        if (!checkbox) return
        checkbox.checked = !checkbox.checked
        checkbox.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })

    syncAllItems()
  })

  if (window.productVariantInteractionInit !== true) {
    window.productVariantInteractionInit = true

    window.addEventListener('kt.product.variant.change', (event) => {
      if (!event.detail || !window.gsap) return

      const gallery = document.querySelector('[data-product-media-tilt]')
      if (!gallery) return

      window.gsap.fromTo(gallery, {
        autoAlpha: 0.82,
        scale: 0.99
      }, {
        autoAlpha: 1,
        scale: 1,
        duration: 0.34,
        ease: 'power2.out'
      })
    }, { once: false })
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initProductPageMotion()
    initProductComponentInteractions()
  })
} else {
  initProductPageMotion()
  initProductComponentInteractions()
}

document.addEventListener('shopify:section:load', (event) => {
  initProductPageMotion(event.target || document)
  initProductComponentInteractions(event.target || document)
})
